import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { dateFnsLocale } from "@/lib/dateLocale";
import { formatShortRange } from "@/lib/format";
import { localeOf, pick } from "@/lib/languages";
import { cn } from "@/lib/utils";
import { appShellL } from "@/i18n/appShell";
import { searchFormL } from "@/i18n/search";
import AppSheet, { APP_SHEET_BODY, APP_SHEET_FOOTER } from "./AppSheet";

// This month and the next twelve: a year of trips ahead, scrolled to the chosen one.
const MONTHS = 13;
const dayOf = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const sameDay = (a, b) => Boolean(a && b) && dayOf(a).getTime() === dayOf(b).getTime();
const daysBetween = (a, b) => Math.round((dayOf(b) - dayOf(a)) / 86400000);
const capital = (text, locale) => text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);

/**
 * The dates sheet of the apps (the approved design, 04-home-dates): the
 * weekdays fixed on top, the months one under the other, the chosen days as
 * night-blue pills with the nights between them tinted, and below "12 – 16
 * nov. · 4 nuits" with a night-blue "Valider". The choice is a draft until
 * then; closing the sheet any other way keeps the dates as they were. A
 * single date (`mode="single"`) is taken at the first touch, as the site's
 * own date field does.
 */
export default function AppDatesSheet({ open, onOpenChange, title, testId = "dates-sheet", ...picker }) {
  return (
    <AppSheet open={open} onOpenChange={onOpenChange} title={title} bare data-testid={testId}>
      <DatesPicker {...picker} testId={testId} close={() => onOpenChange(false)} />
    </AppSheet>
  );
}

/**
 * The sheet's content, mounted at each opening from the form's dates. Picking
 * follows the site's calendar (DateFields.DateRangeBox): the touch after a
 * first date that is not earlier is the second date; any other touch starts
 * again, keeping a second date that still comes after it.
 */
function DatesPicker({ mode = "range", from, to, minDate, labelFrom, labelTo, startWith = "from", unit = "nights", onConfirm, close, testId }) {
  const { lang } = useSettings();
  const l = pick(appShellL, lang).sheets;
  const words = pick(searchFormL, lang);
  const locale = localeOf(lang);
  const [draft, setDraft] = useState({ from, to });
  const [next, setNext] = useState(startWith === "to" && from ? "to" : "from");
  const chosenMonth = useRef(null);
  const [first] = useState(() => dayOf(minDate || new Date()));

  const { months, weekdays, format } = useMemo(() => {
    const weekStartsOn = dateFnsLocale(lang)?.options?.weekStartsOn ?? 1;
    const shown = from && dayOf(from) >= first ? dayOf(from) : first;
    const ahead = (shown.getFullYear() - first.getFullYear()) * 12 + shown.getMonth() - first.getMonth();
    const monthName = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
    const list = Array.from({ length: Math.max(MONTHS, ahead + 2) }, (_, i) => {
      const month = new Date(first.getFullYear(), first.getMonth() + i, 1);
      const length = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
      return {
        key: `${month.getFullYear()}-${month.getMonth()}`,
        name: capital(monthName.format(month), locale),
        offset: (month.getDay() - weekStartsOn + 7) % 7,
        days: Array.from({ length }, (_, d) => new Date(month.getFullYear(), month.getMonth(), d + 1)),
        holdsChosen: i === ahead,
      };
    });
    const narrow = new Intl.DateTimeFormat(locale, { weekday: "narrow" });
    return {
      months: list,
      // 1 January 2023 was a Sunday.
      weekdays: Array.from({ length: 7 }, (_, i) => narrow.format(new Date(2023, 0, 1 + ((weekStartsOn + i) % 7)))),
      format: {
        day: new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
        short: new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }),
      },
    };
  }, [first, from, lang, locale]);

  // The sheet opens on the month of the first chosen date.
  useEffect(() => {
    const frame = requestAnimationFrame(() => chosenMonth.current?.scrollIntoView?.({ block: "start" }));
    return () => cancelAnimationFrame(frame);
  }, []);

  const choose = (day) => {
    if (mode === "single") {
      onConfirm({ from: day });
      close();
      return;
    }
    if (next === "to" && draft.from && day >= dayOf(draft.from)) {
      setDraft({ from: draft.from, to: day });
      setNext("from");
      return;
    }
    const keptTo = draft.to && dayOf(draft.to) > day ? draft.to : undefined;
    setDraft({ from: day, to: keptTo });
    setNext(keptTo ? "from" : "to");
  };

  const both = Boolean(draft.from && draft.to);
  const span = both && !sameDay(draft.from, draft.to);
  const count = both ? daysBetween(draft.from, draft.to) : 0;
  const summary = both ? formatShortRange(dayOf(draft.from), dayOf(draft.to), { lang }) : draft.from ? format.short.format(draft.from) : "";
  const prompt = words.pickPrompt(String((draft.from ? labelTo : labelFrom) || "").toLowerCase());

  return (
    <>
      <div className={cn(APP_SHEET_BODY, "pt-0")}>
        <div className="tk-cal-head" aria-hidden="true">
          {weekdays.map((name, i) => <span key={i}>{name}</span>)}
        </div>
        {months.map((month) => (
          <section key={month.key} ref={month.holdsChosen ? chosenMonth : undefined} className="tk-cal-section" aria-label={month.name}>
            <h3 className="tk-cal-month">{month.name}</h3>
            <div className="tk-cal">
              {month.offset ? <span style={{ gridColumn: `span ${month.offset}` }} aria-hidden="true" /> : null}
              {month.days.map((day) => {
                const start = sameDay(day, draft.from);
                const end = !start && sameDay(day, draft.to);
                return (
                  <button key={day.getDate()} type="button" className="tk-cal-day" disabled={day < first}
                    aria-label={format.day.format(day)} aria-pressed={start || sameDay(day, draft.to)}
                    aria-current={sameDay(day, new Date()) ? "date" : undefined}
                    data-end={start ? "start" : end ? "end" : undefined} data-span={span && (start || end) ? "" : undefined}
                    data-in={span && day > dayOf(draft.from) && day < dayOf(draft.to) ? "" : undefined}
                    data-testid={`${testId}-day-${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}`}
                    onClick={() => choose(day)}>
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      {mode === "range" ? (
        <div className={APP_SHEET_FOOTER}>
          <div className="min-w-0 flex-1" aria-live="polite">
            <b className="block truncate text-body-md font-black text-touki-black tabular-nums" data-testid={`${testId}-summary`}>{summary || prompt}</b>
            <span className="block text-caption text-touki-muted" data-testid={`${testId}-count`}>
              {both ? (unit === "days" ? l.days(count) : l.nights(count)) : summary ? prompt : ""}
            </span>
          </div>
          <button type="button" disabled={!both} className="tk-app-btn tk-press" data-testid={`${testId}-confirm`}
            onClick={() => { onConfirm({ from: draft.from, to: draft.to }); close(); }}>
            {l.validate}
          </button>
        </div>
      ) : null}
    </>
  );
}
